import angular from "angular";

import { ApiValidation, CalculationRowType, DataMapper, Dataset, DatasetListItem, Datasets, Employment, Employments, FileParser, HrSuuntiImportLogic, Importable, RowTypeMatch, SalaryBasesRow, ValidationHelper } from "@salaxy/core";
import { SalaryBases } from "./SalaryBases";
import { EditDialogKnownActions, JsonSchemaService, UiCrudHelpers, UiHelpers } from "../../../services";
import { PayrollCrudController } from "../PayrollCrudController";
import { AjaxNg1 } from "../../../ajax";
import { ImportController } from "../../helpers/ImportController";

/**
 * Controller for employer and worker charts using angular-chart
 */
export class SalaryBasesController implements angular.IController {

  /*
  TODO-lista:
  - Uuden rivin luominen: Eri tyypit (ignore, warning etc.)
  - Warning ja Error toimimaan
  - TODO ja HACK
  - Saako rivin muokkauksen dialogiin (Usecase)
  - Rivin muokkaus Rivit-näkymään (rowType-dialogi)
  */

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Employments", "UiCrudHelpers", "UiHelpers", "AjaxNg1", "JsonSchemaService", "Datasets"];

  /** Source data that is mapped to salary bases suitable to creating calculations etc. */
  public current: SalaryBases;

  /** Full set of employments for binding to data. */
  public employments: Employment[];

  /** Datasets that define the import pattern / settings. */
  public datasets: DatasetListItem[];

  /** Current dataset that contains the definition data for the import. */
  public dataset: Dataset;

  /** Payroll controller that contains the payroll that is being edited. */
  public payrollController: PayrollCrudController;

  public importController: ImportController;

  /** Active tab: set by the import operations. */
  public currentTab: string;

  /** TODO: Move to enum, also add descriptions */
  public matchTypes = {
    row: "Oletusrivi",
    hourlyRow: "Tuntipalkkaan perustuva rivi",
    monthlySalary: "Kuukausipalkka",
    ignore: "Ohitetaan",
    warning: "Varoitus",
    error: "Virhe",
  };

  /**
   * Creates a new SalaryBasesController
   * @ignore
   */
  constructor(
    private employmentsApi: Employments,
    private uiCrudHelpers: UiCrudHelpers,
    private uiHelpers: UiHelpers,
    private ajax: AjaxNg1,
    private jsonSchemaService: JsonSchemaService,
    private datasetsApi: Datasets
    ) {
    // Dependency injection
  }

  /** Initializes the controller. */
  public $onInit() {
    this.current = {};
    this.datasetsApi.getOData({
      // TODO: Perhaps add `status eq "draft/instance/template"`
      $filter: `entityType eq 'import'`,
    }).then((result) => {
      this.datasets = result.items;
    });
  }

  /**
   * Loads the given dataset.
   * @param datasetToLoad List item for which the whole dataset should be loaded.
   */
  public loadDataset(datasetToLoad: DatasetListItem): void {
    const loader = this.uiHelpers.showLoading();
    this.datasetsApi.getSingle(datasetToLoad.id).then((result) => {
      this.dataset = result;
      loader.dismiss();
    });
  }

  /** The row types mapper from the dataset */
  public getRowTypeMapper(): RowTypeMatch[] {
    if (!this.dataset.data[0]?.content?.length) {
      this.dataset.data[0].content = HrSuuntiImportLogic.getRowTypeMapperEditable();
    }
    return this.dataset.data[0].content;
  }

  /** Fetches the employment relations for operations on this UI. */
  public getEmployments() {
    const loader = this.uiHelpers.showLoading("Ladataan työsuhteita...");
    this.employmentsApi.getActiveFullObject(null).then((result) => {
      if (this.payrollController?.current) {
        const payrollEmployments = this.payrollController.current.calcs.map((x) => x.worker.employmentId);
        result.forEach((x) => {
          if (payrollEmployments.find((id) => x.id == id)) {
            x.usecase.data = x.usecase.data || {};
            x.usecase.data.isPayroll = true;
          }
        });
      }
      this.employments = result;
      this.currentTab = "employments";
      loader.dismiss();
    });
  }

  /** Gets the importable interface for importing the SalaryBasesRows */
  public getImportable(): Importable<SalaryBasesRow> {
    const result: Importable<SalaryBasesRow> = {
      getImportMappers() {
        return [{
          value: "NG1/SalaryBasesController/HRSuunti",
          text: "HR Suunti / Tikon tuonti",
        }];
      },
      getImportMapper: async () => {
        const mapper = new DataMapper<any, SalaryBasesRow>("NG1/SalaryBasesController/HRSuunti", "HR Suunti / Tikon tuonti");
        const map = {
          PVM: "date",
          TYNRO: "srcEmploymentId",
          PLNRO: "srcRowTypeId",
          TVR: null,
          "KPL/MISTÄ": "count",
          "AIKA/MIHIN": null,
          "MK/EUR": null,
          "OSASTO": "costCenter",
        };
        await mapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "SalaryBasesRow", "target");
        mapper.setPaths(map);
        mapper.generateSchema("HRSuuntiTikon", "source");
        return mapper;
      },
      parsers: FileParser.getDefaultParsers(),
      finalize: (items) => {
        this.current.rows = items;
        this.currentTab = "rows";
        return Promise.resolve();
      },
      validate: (item: SalaryBasesRow, ix): Promise<{item: SalaryBasesRow, validation: ApiValidation }> => {
        const validation: ApiValidation = {};
        const helper = new ValidationHelper(validation);
        if (this.employments?.length) {
          HrSuuntiImportLogic.resolveEmployment(item, this.employments);
          helper.checkNotEmpty(item, "employmentId", `Työntekijää ei löydy: ${item.srcEmploymentId}.`);
        } else {
          if (ix == 0) {
            helper.error("Ei työsuhteita - tietoja ei voi tuoda! Hae ensin työsuhteet ja tuo rivit vasta sen jälkeen.", "Ei työsuhteita");
          }
        }

        helper.checkNotEmpty(item, "srcRowTypeId", "TYNRO (srcRowTypeId) puuttuu");
        helper.checkNotEmpty(item, "date", "PVM (date) puuttuu");
        helper.checkNotEmpty(item, "srcRowTypeId", "PLNRO (srcRowTypeId) puuttuu");
        helper.checkNotEmpty(item, "count", "KPL/MISTÄ (count) puuttuu");
        helper.checkNotEmpty(item, "costCenter", "OSASTO (costCenter) puuttuu");
        helper.recalc();
        return Promise.resolve({ item, validation });
      },
    };
    return result;
  }

  /** Saves the dataset. */
  public save(): void {
    const loader = this.uiHelpers.showLoading("Tallennetaan");
    this.dataset.data[0].content.forEach((row) => {
      Object.keys(row).filter((key) => key[0] == "$") .forEach((key) => {
        delete row[key];
      });
    });
    this.datasetsApi.save(this.dataset).then((result) => {
      this.dataset = result;
      loader.dismiss();
    });
  }

  /** Processes the import  */
  public processImport() {
    if (!this.current.rows?.length) {
      alert("Hae ensin työsuhteet (1.) ja lataa data (2.)!");
      return;
    }

    // TODO: Copying is done so that String => RegEx is not saved. Handle this in core.
    const mapper = angular.copy(this.getRowTypeMapper());
    HrSuuntiImportLogic.resolveTypesAndPrices(this.current.rows, mapper);
    this.currentTab = "rows";
  }

  /** Saves the rows to the given payroll */
  public saveToPayroll() {
    if ((this.payrollController?.currentId || "new") == "new") {
      alert("Palkkalista ei ole vielä ladattu! Ei voi tallentaa!");
      return;
    }
    if (!this.current?.rows?.length) {
      alert("Ei rivejä!");
      return;
    }
    const rows = this.current.rows.filter((x) => x.rowType && x.rowType != CalculationRowType.Unknown);
    const loader = this.uiHelpers.showLoading("Tallennetaan palkkalistaa");
    HrSuuntiImportLogic.addToPayroll(this.payrollController.currentId, rows, this.employments, this.ajax).then((x) => {
      loader.dismiss();
      alert("Rivit on tallennettu palkkalistaan: " + x.id);
      this.currentTab = "payroll";
      this.payrollController.reload();
    });
  }


  /** Shows a dialog box for editing the employment. */
  public editEmployment(row: Employment) {
    this.uiCrudHelpers.openEditEmploymentDialog(row.id, "updateCalc").then((result) => {
      if (result.action == EditDialogKnownActions.Ok) {
        angular.merge(row, result.item);
      }
    });
  }
}
